import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import {
  ChevronRight, ChevronDown, Plus, Search, Sparkles, Target, User, X, Upload,
  ListChecks, Loader2, LayoutGrid, ArrowUpDown, Trash2, Edit2, CheckCircle2, Circle, Send,
} from "lucide-react";
import { StatusDot, StatusBadge, HealthBar, CommitmentBadge } from "@/components/Badges";
import { formatDate } from "@/lib/utils-fos";
import { ChannelIcon } from "@/components/BrandIcons";
import { toast } from "sonner";
import { KebabMenu } from "@/components/KebabMenu";
import { PagedKebabMenu } from "@/components/PagedKebabMenu";
import { CsvImportModal } from "@/components/CsvImportModal";
import { KanbanBoard } from "@/components/KanbanBoard";
import { PersonLink } from "@/components/PersonLink";
import { useWorkspace } from "@/context/WorkspaceContext";
import { NewProjectModal } from "@/components/ProjectsSidebar";
import { Tip } from "@/components/ui/tooltip";
import { ReachOutModal } from "@/pages/Inbox";
import { extractAssistantDraft } from "@/pages/AskAnything";

// Projects page: two tabs — Tree (detail view driven by ?focus=) and Kanban (?scope=).
// The tree itself lives in the Layout secondary nav (ProjectsSidebar).

const STATUS_OPTS = ["todo", "in_progress", "blocked", "done"];
const PRIORITY_OPTS = ["low", "medium", "high", "critical"];

export default function Projects() {
  const nav = useNavigate();
  const [tab, setTab] = useState("tree"); // tree | kanban  (tree = Tasks view)
  const { tree, people, sops, reload } = useWorkspace();
  const [params] = useSearchParams();
  const focus = params.get("focus") || "";
  const scope = params.get("scope") || "all";
  const [showNewProject, setShowNewProject] = useState(false);

  // NOTE: intentionally do NOT auto-switch tab on scope change — user requested
  // that the active tab (Tasks/Kanban) is retained while navigating between
  // verticals, categories, and projects.

  return (
    <div className="flex flex-col h-full pb-16 md:pb-0">
      <div className="flex items-center gap-2 px-4 md:px-6 border-b border-neutral-200 bg-white shrink-0">
        <TabBtn active={tab === "tree"} onClick={() => setTab("tree")} testid="projects-tab-tree">Tasks</TabBtn>
        <TabBtn active={tab === "kanban"} onClick={() => setTab("kanban")} testid="projects-tab-kanban">Kanban</TabBtn>
        <div className="ml-auto flex items-center gap-3 py-2">
          <span className="text-xs text-neutral-500 hidden sm:inline">{tree.projects.length} projects · {tree.tasks.length} tasks</span>
          <Tip label="Create a new project — choose which vertical/category to place it under">
            <button
              onClick={() => setShowNewProject(true)}
              data-testid="projects-header-new-project"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white border border-neutral-200 text-blue-600 hover:bg-neutral-50 font-semibold transition-colors"
            >
              <Plus size={12} /> New project
            </button>
          </Tip>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" data-testid="projects-scroll-container">
        {tab === "tree"
          ? <TreeDetail tree={tree} people={people} sops={sops} focus={focus} scope={scope} reload={reload} />
          : <ProjectsKanban tree={tree} people={people} scope={scope} reload={reload} />}
      </div>
      {showNewProject && (
        <NewProjectModal
          verticals={tree.folders}
          categories={tree.groups}
          initialVerticalId={tree.folders[0]?.id || ""}
          initialCategoryId=""
          onClose={() => setShowNewProject(false)}
          onCreated={async (createdId) => {
            setShowNewProject(false);
            await reload();
            if (createdId) nav(`/projects?focus=${createdId}&scope=project:${createdId}`);
          }}
        />
      )}
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-neutral-900 text-neutral-900" : "border-transparent text-neutral-500 hover:text-neutral-900"
      }`}
    >{children}</button>
  );
}

// ============================================================================
// ScopedTaskList — the primary Tree-tab view when no project is focused.
// Shows all tasks in the selected scope (all / vertical / category) with
// checkbox + toolbar (search, priority filter, status filter, sort) + kebab.
// The user's mental model is "tasks-first" — projects are just tags/grouping.
// ============================================================================
function ScopedTaskList({ tree, people, scope, reload }) {
  const nav = useNavigate();
  const [tasks, setTasks] = useState(tree.tasks);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("priority"); // priority | due | created
  const [priorityFilter, setPriorityFilter] = useState(""); // "" | low | medium | high | critical
  const [statusFilter, setStatusFilter] = useState(""); // "" | todo | in_progress | blocked | done
  const [showDone, setShowDone] = useState(false);

  useEffect(() => { setTasks(tree.tasks); }, [tree.tasks]);

  // Compute scope label + which projects are in scope
  const scopeInfo = useMemo(() => {
    if (scope === "all") return { label: "All Verticals", projectIds: null };
    if (scope.startsWith("vertical:")) {
      const fid = scope.split(":")[1];
      const f = tree.folders.find((x) => x.id === fid);
      const pids = tree.projects.filter((p) => p.folder_id === fid).map((p) => p.id);
      return { label: f?.name || "Vertical", projectIds: new Set(pids), color: f?.color };
    }
    if (scope.startsWith("category:")) {
      const gid = scope.split(":")[1];
      const g = tree.groups.find((x) => x.id === gid);
      const f = tree.folders.find((x) => x.id === g?.folder_id);
      const pids = tree.projects.filter((p) => p.group_id === gid).map((p) => p.id);
      return { label: g?.name || "Category", projectIds: new Set(pids), color: g?.color || f?.color, parent: f?.name };
    }
    return { label: "All Verticals", projectIds: null };
  }, [tree, scope]);

  const scopedTasks = useMemo(() => {
    let out = scopeInfo.projectIds
      ? tasks.filter((t) => scopeInfo.projectIds.has(t.project_id))
      : tasks;
    if (!showDone) out = out.filter((t) => t.status !== "done");
    if (statusFilter) out = out.filter((t) => t.status === statusFilter);
    if (priorityFilter) out = out.filter((t) => t.priority === priorityFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((t) => (t.title || "").toLowerCase().includes(s));
    }
    // Sort
    const priorityRank = { critical: 3, high: 2, medium: 1, low: 0 };
    out = [...out].sort((a, b) => {
      if (sortBy === "priority") {
        return (priorityRank[b.priority || "low"] || 0) - (priorityRank[a.priority || "low"] || 0);
      }
      if (sortBy === "due") {
        const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return ad - bd;
      }
      // created
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
    return out;
  }, [tasks, scopeInfo, q, sortBy, priorityFilter, statusFilter, showDone]);

  const toggleDone = async (task) => {
    const nextStatus = task.status === "done" ? "todo" : "done";
    await endpoints.updateTask(task.id, { status: nextStatus, completed_at: nextStatus === "done" ? new Date().toISOString() : null });
    setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
  };
  const removeTask = async (task) => {
    if (!confirm(`Delete "${task.title}"?`)) return;
    await endpoints.deleteTask(task.id);
    setTasks(tasks.filter((t) => t.id !== task.id));
    toast.success("Task deleted");
  };
  const changePriority = async (task, priority) => {
    await endpoints.updateTask(task.id, { priority });
    setTasks(tasks.map((t) => (t.id === task.id ? { ...t, priority } : t)));
  };
  const changeStatus = async (task, status) => {
    await endpoints.updateTask(task.id, { status });
    setTasks(tasks.map((t) => (t.id === task.id ? { ...t, status } : t)));
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto animate-fade-in" data-testid="scoped-task-list">
      {/* Heading */}
      <div className="mb-4">
        <p className="text-[10px] mono uppercase tracking-widest text-neutral-500">Viewing tasks in</p>
        <h1 className="text-2xl font-semibold tracking-tight mt-0.5 flex items-center gap-2" data-testid="scope-heading">
          {scopeInfo.color && <span className="h-2.5 w-2.5 rounded-sm" style={{ background: scopeInfo.color }} />}
          {scopeInfo.parent && <span className="text-neutral-500 font-normal">{scopeInfo.parent} ·</span>}
          {scopeInfo.label}
        </h1>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3 py-1.5 rounded-md border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks…"
            className="flex-1 bg-transparent outline-none text-sm"
            data-testid="task-list-search"
          />
        </div>
        <FilterSelect value={priorityFilter} onChange={setPriorityFilter} label="Priority" options={["low","medium","high","critical"]} testid="task-filter-priority" />
        <FilterSelect value={statusFilter} onChange={setStatusFilter} label="Status" options={["todo","in_progress","blocked","done"]} testid="task-filter-status" />
        <Tip label="Sort tasks">
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-neutral-200 bg-white">
            <ArrowUpDown size={12} className="text-neutral-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent outline-none text-xs"
              data-testid="task-sort"
            >
              <option value="priority">Priority</option>
              <option value="due">Due date</option>
              <option value="created">Newest</option>
            </select>
          </div>
        </Tip>
        <Tip label={showDone ? "Hide done tasks" : "Show done tasks too"}>
          <button
            onClick={() => setShowDone(!showDone)}
            data-testid="task-toggle-done"
            className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors ${showDone ? "border-neutral-900 text-neutral-900 bg-neutral-50" : "border-neutral-200 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50"}`}
          >
            {showDone ? "Showing done" : "Hide done"}
          </button>
        </Tip>
      </div>
      <div className="text-[11px] mono text-neutral-500 mb-2" data-testid="task-list-count">
        {scopedTasks.length} task{scopedTasks.length === 1 ? "" : "s"}
      </div>

      {/* List */}
      {scopedTasks.length === 0 ? (
        <div className="fos-card p-8 text-center">
          <ListChecks size={20} className="mx-auto mb-2 text-neutral-400" />
          <p className="text-sm text-neutral-500">No tasks in this scope.</p>
        </div>
      ) : (
        <div className="fos-card divide-y divide-neutral-100 overflow-hidden">
          {scopedTasks.map((t) => (
            <ScopedTaskRow
              key={t.id}
              task={t}
              tree={tree}
              people={people}
              onToggle={() => toggleDone(t)}
              onDelete={() => removeTask(t)}
              onChangePriority={(p) => changePriority(t, p)}
              onChangeStatus={(s) => changeStatus(t, s)}
              onOpenProject={() => nav(`/projects?focus=${t.project_id}&scope=project:${t.project_id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, label, options, testid }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-neutral-200 bg-white">
      <span className="text-[10px] mono uppercase text-neutral-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent outline-none text-xs capitalize"
        data-testid={testid}
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o} className="capitalize">{o.replace("_", " ")}</option>)}
      </select>
    </div>
  );
}

function ScopedTaskRow({ task, tree, people, onToggle, onDelete, onChangePriority, onChangeStatus, onOpenProject }) {
  const project = tree.projects.find((p) => p.id === task.project_id);
  const folder = tree.folders.find((f) => f.id === project?.folder_id);
  const owner = people?.find((p) => p.id === task.assignee_id);
  const priorityColors = { critical: "bg-red-50 text-red-700", high: "bg-orange-50 text-orange-700", medium: "bg-amber-50 text-amber-700", low: "bg-neutral-100 text-neutral-600" };
  const isDone = task.status === "done";
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 transition-colors ${isDone ? "opacity-60" : ""}`} data-testid={`task-row-${task.id}`}>
      <Tip label={isDone ? "Mark as todo" : "Mark as done"}>
        <button onClick={onToggle} data-testid={`task-check-${task.id}`} className="shrink-0">
          {isDone ? <CheckCircle2 size={16} className="text-emerald-600" /> : <Circle size={16} className="text-neutral-300 hover:text-neutral-700" />}
        </button>
      </Tip>
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isDone ? "line-through" : ""}`}>{task.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px] text-neutral-500">
          {project && (
            <button
              onClick={onOpenProject}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-neutral-200/60 transition-colors"
              data-testid={`task-project-tag-${task.id}`}
            >
              <span className="h-1.5 w-1.5 rounded-sm" style={{ background: folder?.color || "#A3A3A3" }} />
              <span className="text-neutral-700">{project.name}</span>
            </button>
          )}
          {task.due_date && <span className="mono">Due {formatDate(task.due_date)}</span>}
          {owner && <span>@ {owner.name}</span>}
          <span className="mono uppercase text-[10px]">{(task.status || "todo").replace("_", " ")}</span>
        </div>
      </div>
      <span className={`text-[10px] px-2 py-0.5 rounded font-mono uppercase ${priorityColors[task.priority] || priorityColors.low}`}>
        {task.priority || "low"}
      </span>
      <KebabMenu
        size={13}
        testid={`task-kebab-${task.id}`}
        tooltip="Task actions"
        items={[
          { label: "Open project", icon: <LayoutGrid size={12} />, onClick: onOpenProject, description: "Jump to this task's parent project", testid: `task-action-open-${task.id}` },
          { label: "Set priority: Critical", icon: <span className="h-2 w-2 rounded-full bg-red-500" />, onClick: () => onChangePriority("critical"), description: "Highest attention — top of the sort" },
          { label: "Set priority: High", icon: <span className="h-2 w-2 rounded-full bg-orange-500" />, onClick: () => onChangePriority("high") },
          { label: "Set priority: Medium", icon: <span className="h-2 w-2 rounded-full bg-amber-500" />, onClick: () => onChangePriority("medium") },
          { label: "Set status: In progress", icon: <Edit2 size={12} />, onClick: () => onChangeStatus("in_progress"), description: "Actively working on it" },
          { label: "Set status: Blocked", icon: <Edit2 size={12} />, onClick: () => onChangeStatus("blocked"), description: "Waiting on someone/something" },
          { label: "Delete", icon: <Trash2 size={12} />, onClick: onDelete, danger: true, testid: `task-action-delete-${task.id}`, description: "Permanent — cannot be undone" },
        ]}
      />
    </div>
  );
}

// ============================================================================
// Tree tab — shows detail of the currently focused project.
// If no focus, ScopedTaskList (above) is rendered instead.
// ============================================================================
function TreeDetail({ tree, people, sops, focus, scope, reload }) {
  const nav = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [projTasks, setProjTasks] = useState([]);
  const [projEvents, setProjEvents] = useState([]);
  const [projCommitments, setProjCommitments] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [modal, setModal] = useState(null);
  const [analysis, setAnalysis] = useState("");
  const [outreach, setOutreach] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [outreachLoading, setOutreachLoading] = useState(false);
  // Send-now flow for the AI recommendation panel. Pulls the draft body out of
  // the recommendation text and — if the "Reach out to: <Name>" line resolves
  // to someone in the workspace — pre-selects them in the compose modal.
  const [outreachReachOut, setOutreachReachOut] = useState(null);

  const openOutreachSendNow = () => {
    const body = extractAssistantDraft(outreach);
    if (!body) {
      toast.error("Couldn't detect a draft in the recommendation. Try re-running.");
      return;
    }
    // Try to resolve the target person from a "**Reach out to:** <name>" line.
    let targetPerson = null;
    const m = outreach.match(/reach\s*out\s*to\s*:?\s*\**\s*([^\n(]+?)(?:\s*\(|\n|$)/i);
    if (m && m[1]) {
      const nameLow = m[1].trim().toLowerCase();
      targetPerson = people.find((p) => (p.name || "").toLowerCase() === nameLow)
        || people.find((p) => nameLow && (p.name || "").toLowerCase().includes(nameLow))
        || null;
    }
    const platform = (targetPerson?.preferred_channel || targetPerson?.communication_preference || "email").toLowerCase();
    setOutreachReachOut({
      platform: ["email", "slack", "whatsapp", "linkedin", "twitter", "sms"].includes(platform) ? platform : "email",
      to_person_id: targetPerson?.id || "",
      to_display: targetPerson?.email || "",
      subject: "",
      body,
      related_project_id: selectedProject?.id || null,
    });
  };
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!focus) { setSelectedProject(null); setProjTasks([]); setProjEvents([]); setProjCommitments([]); setLoadError(""); return; }
    let cancelled = false;
    // Reset detail state so we always render a fresh loading UI on focus change
    setSelectedProject(null);
    setLoadError("");
    setLoading(true);
    (async () => {
      try {
        const [proj, allTasks, allEvents, allCommits] = await Promise.all([
          endpoints.getProject(focus), endpoints.tasks(), endpoints.events(), endpoints.commitments(),
        ]);
        if (cancelled) return;
        setSelectedProject(proj);
        setProjTasks(allTasks.filter((t) => t.project_id === focus));
        setProjEvents(allEvents.filter((e) => e.project_ids?.includes(focus)).slice(0, 10));
        setProjCommitments(allCommits.filter((c) => c.project_id === focus));
        setAnalysis(""); setOutreach("");
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.status === 404 ? "Project not found." : "Failed to load project.";
          setLoadError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [focus]);

  const scopedProjects = useMemo(() => {
    if (scope === "all") return tree.projects;
    if (scope.startsWith("vertical:")) {
      const fid = scope.split(":")[1];
      return tree.projects.filter((p) => p.folder_id === fid);
    }
    if (scope.startsWith("category:")) {
      const gid = scope.split(":")[1];
      return tree.projects.filter((p) => p.group_id === gid);
    }
    if (scope.startsWith("project:")) {
      const pid = scope.split(":")[1];
      return tree.projects.filter((p) => p.id === pid);
    }
    return tree.projects;
  }, [tree.projects, scope]);

  const runAnalyze = async () => {
    if (!selectedProject) return;
    setAnalysisLoading(true); setAnalysis("");
    try { const r = await endpoints.projectAnalyze(selectedProject.id); setAnalysis(r.analysis || ""); }
    catch (e) { toast.error("AI analysis failed"); }
    finally { setAnalysisLoading(false); }
  };
  const runOutreach = async () => {
    if (!selectedProject) return;
    setOutreachLoading(true); setOutreach("");
    try { const r = await endpoints.projectOutreach(selectedProject.id); setOutreach(r.recommendation || ""); }
    catch (e) { toast.error("AI outreach failed"); }
    finally { setOutreachLoading(false); }
  };

  const toggleTaskStatus = async (task) => {
    const nextStatus = task._next || (task.status === "done" ? "todo" : "done");
    await endpoints.updateTask(task.id, {
      status: nextStatus,
      completed_at: nextStatus === "done" ? new Date().toISOString() : null,
    });
    setProjTasks(projTasks.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
    if (task._next) toast.success(`Task moved to ${nextStatus.replace("_", " ")}`);
  };
  const addTask = async () => {
    if (!newTaskTitle.trim() || !selectedProject) return;
    const t = await endpoints.createTask({ title: newTaskTitle, project_id: selectedProject.id, status: "todo", priority: "medium" });
    setProjTasks([t, ...projTasks]);
    setNewTaskTitle("");
    toast.success("Task added");
  };
  const addSubtask = async (parent, title) => {
    if (!title?.trim() || !selectedProject) return;
    const t = await endpoints.createTask({
      title, project_id: selectedProject.id, parent_task_id: parent.id, status: "todo", priority: "medium",
    });
    setProjTasks([...projTasks, t]);
    toast.success("Subtask added");
  };
  const addTaskFromSop = async ({ sop_id, priority, owner, parent_title }) => {
    if (!selectedProject) return;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/from-sop`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ project_id: selectedProject.id, sop_id, priority, owner, parent_title }),
      });
      if (!r.ok) throw new Error("failed");
      toast.success("Task template added with subtasks");
      const allTasks = await endpoints.tasks();
      setProjTasks(allTasks.filter((t) => t.project_id === selectedProject.id));
    } catch (e) { toast.error("Could not add SOP as task"); }
  };
  const updateTask = async (task, patch) => {
    await endpoints.updateTask(task.id, patch);
    setProjTasks(projTasks.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));
  };
  const deleteTask = async (task) => {
    await endpoints.deleteTask(task.id);
    setProjTasks(projTasks.filter((t) => t.id !== task.id && t.parent_task_id !== task.id));
    toast.success("Task deleted");
  };

  if (!focus) {
    return (
      <ScopedTaskList tree={tree} people={people} scope={scope} reload={reload} />
    );
  }

  if (loading) {
    return <div className="p-8 flex items-center justify-center text-neutral-500 text-sm gap-2"><Loader2 size={14} className="animate-spin" /> Loading project…</div>;
  }

  if (loadError || !selectedProject) {
    return (
      <div className="p-8 max-w-md mx-auto text-center">
        <p className="text-sm text-neutral-600 mb-3">{loadError || "This project could not be loaded."}</p>
        <button
          onClick={() => nav("/projects")}
          className="text-sm px-3 py-1.5 rounded-md text-blue-600 hover:text-blue-800 font-semibold"
          data-testid="tree-back-to-all"
        >← Back to all projects</button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA]">
      <div className="p-4 md:p-6 max-w-5xl animate-fade-in">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={selectedProject.status} />
            {selectedProject.owner && (
              <span className="text-xs text-neutral-500">Owned by <PersonLink name={selectedProject.owner} className="text-xs" /></span>
            )}
          </div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight" data-testid="project-name">
            {selectedProject.name}
          </h1>
        </div>

        <div className="mb-4 pb-4 border-b border-neutral-200 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm" data-testid="project-meta">
          <PlainMeta label="Started" value={formatDate(selectedProject.started_at)} />
          <PlainMeta label="Target" value={formatDate(selectedProject.target_date)} />
          <PlainMeta label="Health" value={<HealthBar score={selectedProject.health_score} />} />
          <PlainMeta label="Owner" value={selectedProject.owner ? <PersonLink name={selectedProject.owner} className="text-sm" /> : "—"} />
          {selectedProject.next_action && (
            <PlainMeta label="Next action" value={selectedProject.next_action} span={4} />
          )}
        </div>

        {selectedProject.stakeholders?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedProject.stakeholders.map((s, i) => (
              <PersonLink key={i} name={s} prefix="@ " className="text-[11px] px-2 py-0.5 rounded bg-neutral-100" />
            ))}
          </div>
        )}

        {selectedProject.current_blockers?.length > 0 && (
          <div className="mb-4 p-3 rounded critical-marker bg-red-50/40 border border-red-100">
            <p className="text-[10px] font-medium text-[#FF453A] uppercase tracking-wider mb-2">Current blockers</p>
            <ul className="space-y-1">
              {selectedProject.current_blockers.map((b, i) => (
                <li key={i} className="text-sm text-neutral-800">· {b}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            data-testid="ai-why-slow"
            onClick={runAnalyze}
            disabled={analysisLoading}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors"
          >
            <Sparkles size={13} className="text-neutral-500" />
            {analysisLoading ? "Analysing…" : "Why is this slow?"}
          </button>
          <button
            data-testid="ai-outreach"
            onClick={runOutreach}
            disabled={outreachLoading}
            className="flex items-center gap-2 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50 transition-colors"
          >
            <User size={13} className="text-neutral-500" />
            {outreachLoading ? "Thinking…" : "Who do I reach out to?"}
          </button>
          <KebabMenu
            testid="project-kebab"
            items={[
              { label: "Import tasks from CSV", icon: <Upload size={13} />, onClick: () => setModal({ kind: "import-tasks" }) },
            ]}
          />
        </div>

        {analysis && (
          <div className="rounded-sm bg-white border border-neutral-200 p-4 mb-4" data-testid="ai-analysis-output">
            <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-2">AI analysis</p>
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed text-neutral-800">{analysis}</pre>
          </div>
        )}
        {outreach && (
          <div className="rounded-sm bg-white border border-neutral-200 p-4 mb-4" data-testid="ai-outreach-output">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-medium text-neutral-500 uppercase tracking-wider">AI recommendation</p>
              <button
                data-testid="ai-outreach-send-now"
                onClick={openOutreachSendNow}
                className="text-xs px-2.5 py-1 rounded-md bg-neutral-900 text-white hover:bg-neutral-800 inline-flex items-center gap-1.5"
              >
                <Send size={11} /> Send now
              </button>
            </div>
            <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed text-neutral-800">{outreach}</pre>
          </div>
        )}

        <TaskList
          tasks={projTasks}
          people={people}
          onToggle={toggleTaskStatus}
          onAddSubtask={addSubtask}
          onUpdate={updateTask}
          onDelete={deleteTask}
          newTaskTitle={newTaskTitle}
          setNewTaskTitle={setNewTaskTitle}
          onAdd={addTask}
          onAddSop={() => setModal({ kind: "sop-task" })}
        />

        {projEvents.length > 0 && (
          <div className="fos-card p-4 mb-4">
            <h3 className="text-sm font-semibold mb-3">Recent activity</h3>
            <div className="space-y-2">
              {projEvents.map((e) => (
                <div key={e.id} className="flex items-start gap-2 text-sm">
                  <ChannelIcon channel={e.source_channel} size={14} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="leading-snug text-neutral-800">{e.content_summary}</p>
                    <p className="text-[11px] mt-0.5 text-neutral-500 capitalize">{e.classification || "fyi"} · {formatDate(e.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {projCommitments.length > 0 && (
          <div className="fos-card p-4 mb-8">
            <h3 className="text-sm font-semibold mb-3">Open commitments</h3>
            <div className="space-y-2">
              {projCommitments.map((c) => (
                <div key={c.id} className="flex items-center gap-2 text-sm p-2 rounded-sm hover:bg-neutral-50">
                  <CommitmentBadge status={c.status} />
                  <p className="flex-1 truncate text-neutral-900">{c.what}</p>
                  <span className="text-[11px] text-neutral-500">{formatDate(c.due_date)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {modal?.kind === "sop-task" && selectedProject && (
        <SopTaskModal
          sops={sops}
          people={people}
          onClose={() => setModal(null)}
          onSubmit={(payload) => { addTaskFromSop(payload); setModal(null); }}
        />
      )}
      {modal?.kind === "import-tasks" && selectedProject && (
        <CsvImportModal resource="tasks" contextOptions={{ projects: tree.projects }} context={{ project_id: selectedProject.id }}
          onClose={() => setModal(null)} onSuccess={async () => { setModal(null); const all = await endpoints.tasks(); setProjTasks(all.filter((t) => t.project_id === selectedProject.id)); }} />
      )}

      {outreachReachOut && (
        <ReachOutModal
          initial={outreachReachOut}
          people={people}
          onClose={() => setOutreachReachOut(null)}
          onSent={() => setOutreachReachOut(null)}
        />
      )}
    </div>
  );
}

function ScopeHeader({ tree, scope }) {
  let label = "All Projects";
  if (scope.startsWith("vertical:")) {
    const f = tree.folders.find((x) => x.id === scope.split(":")[1]);
    label = f ? `Business Vertical · ${f.name}` : "Vertical";
  } else if (scope.startsWith("category:")) {
    const g = tree.groups.find((x) => x.id === scope.split(":")[1]);
    label = g ? `Category · ${g.name}` : "Category";
  }
  return (
    <div className="mb-4">
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-1">Viewing</p>
      <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{label}</h1>
      <p className="text-sm text-neutral-500 mt-1">Pick a project from the sidebar for full detail, or switch to Kanban for a task board.</p>
    </div>
  );
}

function ProjectsList({ projects, tree, onImport }) {
  const nav = useNavigate();
  if (projects.length === 0) {
    return (
      <div className="fos-card p-8 text-center">
        <LayoutGrid size={28} className="mx-auto text-neutral-300 mb-3" />
        <p className="text-sm text-neutral-600 mb-4">No projects in this scope yet.</p>
        <button onClick={onImport} className="text-sm px-3 py-1.5 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50">
          Import projects from CSV
        </button>
      </div>
    );
  }
  return (
    <div className="fos-card divide-y divide-neutral-100">
      {projects.map((p) => {
        const f = tree.folders.find((x) => x.id === p.folder_id);
        const g = tree.groups.find((x) => x.id === p.group_id);
        return (
          <button
            key={p.id}
            onClick={() => nav(`/projects?focus=${p.id}&scope=project:${p.id}`)}
            className="link-row link-row-inline flex items-center gap-3 p-3 w-full text-left"
            data-testid={`projects-list-${p.id}`}
          >
            <StatusDot status={p.status} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-neutral-900">{p.name}</p>
              <p className="text-[11px] text-neutral-500">
                {f?.name}{g && ` · ${g.name}`}{p.owner && ` · ${p.owner}`}
              </p>
            </div>
            <HealthBar score={p.health_score} />
          </button>
        );
      })}
    </div>
  );
}

function PlainMeta({ label, value, span = 1 }) {
  return (
    <div className={span >= 4 ? "col-span-2 md:col-span-4" : span === 2 ? "col-span-2" : ""}>
      <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-neutral-900">{value}</div>
    </div>
  );
}

// ============================================================================
// Task list — parent tasks with expandable subtasks + SOP-as-task
// ============================================================================
function TaskList({ tasks, people, onToggle, onAddSubtask, onUpdate, onDelete, newTaskTitle, setNewTaskTitle, onAdd, onAddSop }) {
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const parents = tasks.filter((t) => !t.parent_task_id);
  const childrenOf = (parentId) => tasks.filter((t) => t.parent_task_id === parentId);

  const counts = { all: parents.length };
  for (const s of ["todo", "in_progress", "blocked", "done"]) counts[s] = parents.filter((t) => t.status === s).length;

  const filteredParents = parents.filter((t) => {
    if (filter !== "all" && t.status !== filter) return false;
    if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const toggleExpanded = (id) => {
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  return (
    <div className="mb-6" data-testid="task-list">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">Tasks</h3>
        <span className="text-xs text-neutral-500">{counts.all} total</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { k: "all", label: "All" },
            { k: "todo", label: "To do" },
            { k: "in_progress", label: "In progress" },
            { k: "blocked", label: "Blocked" },
            { k: "done", label: "Done" },
          ].map(({ k, label }) => (
            <button key={k} onClick={() => setFilter(k)} data-active={filter === k} className="chip" data-testid={`task-filter-${k}`}>
              {label}<span className="ml-1 text-[10px] opacity-70">{counts[k]}</span>
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="flex-1 bg-transparent outline-none text-sm" data-testid="task-search" />
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <input
          value={newTaskTitle}
          onChange={(e) => setNewTaskTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder="Add a task and press enter…"
          className="flex-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500"
          data-testid="new-task-input"
        />
        <button
          onClick={onAdd}
          disabled={!newTaskTitle.trim()}
          className="flex items-center gap-1 text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          data-testid="add-task-btn"
        >
          <Plus size={13} /> Add
        </button>
        <button
          onClick={onAddSop}
          className="flex items-center gap-1 text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white hover:bg-neutral-50"
          data-testid="add-sop-task-btn"
          title="Add a task from a SOP template — steps become subtasks"
        >
          <ListChecks size={13} /> Add from SOP
        </button>
      </div>

      {filteredParents.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-neutral-200 rounded-sm">
          <p className="text-sm text-neutral-500">{parents.length === 0 ? "No tasks yet — add your first above." : "No tasks match this filter."}</p>
        </div>
      ) : (
        <ul className="rounded-sm border border-neutral-200 divide-y divide-neutral-100 bg-white">
          {filteredParents.map((t) => (
            <TaskItem
              key={t.id}
              task={t}
              subtasks={childrenOf(t.id)}
              expanded={expanded.has(t.id)}
              onToggleExpand={() => toggleExpanded(t.id)}
              onToggleStatus={onToggle}
              onAddSubtask={onAddSubtask}
              onUpdate={onUpdate}
              onDelete={onDelete}
              people={people}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskItem({ task, subtasks, expanded, onToggleExpand, onToggleStatus, onAddSubtask, onUpdate, onDelete, people }) {
  const [subInput, setSubInput] = useState("");
  const hasSubs = subtasks && subtasks.length > 0;
  return (
    <>
      <li data-testid={`task-${task.id}`} className="group flex items-start gap-3 p-3 hover:bg-neutral-50 transition-colors">
        <input
          type="checkbox"
          checked={task.status === "done"}
          onChange={() => onToggleStatus(task)}
          className="mt-0.5 h-4 w-4 accent-neutral-900"
          data-testid={`task-check-${task.id}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <button onClick={onToggleExpand} className="p-0.5 text-neutral-400 hover:text-neutral-900" data-testid={`task-expand-${task.id}`}>
              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
            <p className={`text-sm ${task.status === "done" ? "line-through text-neutral-400" : "text-neutral-900"}`}>{task.title}</p>
            {hasSubs && (
              <span className="text-[10px] text-neutral-500">
                · {subtasks.filter((s) => s.status === "done").length}/{subtasks.length}
              </span>
            )}
          </div>
          {(task.due_date || task.status !== "todo" || task.owner) && (
            <p className="text-[11px] text-neutral-500 mt-0.5 flex items-center gap-1.5">
              {task.status !== "todo" && <span className="capitalize">{task.status.replace("_", " ")}</span>}
              {task.owner && <><span>·</span><PersonLink name={task.owner} className="text-[11px]" /></>}
              {task.due_date && <><span>·</span>Due {formatDate(task.due_date)}</>}
            </p>
          )}
        </div>
        <TaskStatusCycle task={task} onToggle={onToggleStatus} />
        <TaskKebab task={task} people={people} onUpdate={onUpdate} onDelete={onDelete} onAddSubtask={() => { if (!expanded) onToggleExpand(); setTimeout(() => document.getElementById(`subin-${task.id}`)?.focus(), 100); }} />
      </li>
      {expanded && (
        <>
          {subtasks.map((s) => (
            <li key={s.id} data-testid={`subtask-${s.id}`} className="group flex items-start gap-3 p-3 pl-10 hover:bg-neutral-50 transition-colors bg-neutral-50/50">
              <input
                type="checkbox"
                checked={s.status === "done"}
                onChange={() => onToggleStatus(s)}
                className="mt-0.5 h-3.5 w-3.5 accent-neutral-900"
                data-testid={`subtask-check-${s.id}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${s.status === "done" ? "line-through text-neutral-400" : "text-neutral-800"}`}>{s.title}</p>
                {(s.owner || s.due_date) && (
                  <p className="text-[10px] text-neutral-500 mt-0.5">
                    {s.owner && <PersonLink name={s.owner} className="text-[10px]" />}
                    {s.owner && s.due_date && " · "}
                    {s.due_date && `Due ${formatDate(s.due_date)}`}
                  </p>
                )}
              </div>
              <TaskStatusCycle task={s} onToggle={onToggleStatus} />
              <TaskKebab task={s} people={people} onUpdate={onUpdate} onDelete={onDelete} />
            </li>
          ))}
          <li className="p-3 pl-10 bg-neutral-50/50">
            <div className="flex items-center gap-2">
              <input
                id={`subin-${task.id}`}
                value={subInput}
                onChange={(e) => setSubInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && subInput.trim()) { onAddSubtask(task, subInput); setSubInput(""); } }}
                placeholder="Add a subtask…"
                className="flex-1 text-xs px-2 py-1.5 rounded-sm border border-neutral-200 bg-white outline-none focus:border-neutral-500"
                data-testid={`subtask-input-${task.id}`}
              />
              <button
                onClick={() => { if (subInput.trim()) { onAddSubtask(task, subInput); setSubInput(""); } }}
                disabled={!subInput.trim()}
                className="text-xs px-2 py-1 rounded-sm bg-neutral-900 text-white disabled:opacity-40"
                data-testid={`subtask-add-${task.id}`}
              >Add</button>
            </div>
          </li>
        </>
      )}
    </>
  );
}

function TaskStatusCycle({ task, onToggle }) {
  const NEXT = { todo: "in_progress", in_progress: "blocked", blocked: "done", done: "todo" };
  const LABEL = { todo: "To do", in_progress: "In progress", blocked: "Blocked", done: "Done" };
  const dot = task.status === "blocked" ? "bg-[#FF453A]" : task.status === "done" ? "bg-neutral-400" : task.status === "in_progress" ? "bg-neutral-900" : "bg-neutral-300";
  return (
    <button
      onClick={() => onToggle({ ...task, _next: NEXT[task.status] })}
      className="text-[10px] px-2 py-0.5 rounded border border-neutral-200 hover:border-neutral-400 flex items-center gap-1.5 text-neutral-600 shrink-0"
      data-testid={`task-cycle-${task.id}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} /> {LABEL[task.status] || task.status}
    </button>
  );
}

function TaskKebab({ task, people, onUpdate, onDelete, onAddSubtask }) {
  const STATUS_LABEL = { todo: "To do", in_progress: "In progress", blocked: "Blocked", done: "Done" };
  const ownerLabel = task.owner || "Unassigned";
  const priorityLabel = task.priority ? task.priority[0].toUpperCase() + task.priority.slice(1) : "Medium";
  const statusLabel = STATUS_LABEL[task.status] || task.status;

  const pages = {
    root: {
      items: [
        onAddSubtask && { label: "Add subtask", icon: <Plus size={13} />, onClick: onAddSubtask, testid: `task-kebab-${task.id}-add-subtask` },
        onAddSubtask && { divider: true },
        { label: "Status", subtitle: statusLabel, toPage: "status", testid: `task-kebab-${task.id}-status` },
        { label: "Priority", subtitle: priorityLabel, toPage: "priority", testid: `task-kebab-${task.id}-priority` },
        { label: "Assign to", subtitle: ownerLabel, toPage: "owner", testid: `task-kebab-${task.id}-assign` },
        { divider: true },
        { label: "Delete task", icon: <X size={13} />, danger: true, onClick: () => onDelete(task), testid: `task-kebab-${task.id}-delete` },
      ].filter(Boolean),
    },
    status: {
      title: "Status",
      items: STATUS_OPTS.map((s) => ({
        label: STATUS_LABEL[s], active: task.status === s,
        onClick: () => onUpdate(task, { status: s, completed_at: s === "done" ? new Date().toISOString() : null }),
      })),
    },
    priority: {
      title: "Priority",
      items: PRIORITY_OPTS.map((p) => ({
        label: p[0].toUpperCase() + p.slice(1), active: task.priority === p,
        onClick: () => onUpdate(task, { priority: p }),
      })),
    },
    owner: {
      title: "Assign to",
      searchable: true,
      searchPlaceholder: "Search people…",
      items: [
        { label: "Unassigned", active: !task.owner, onClick: () => onUpdate(task, { owner: null }) },
        ...(people || []).map((p) => ({ label: p.name, active: task.owner === p.name, onClick: () => onUpdate(task, { owner: p.name }) })),
      ],
    },
  };
  return <PagedKebabMenu size={14} testid={`task-kebab-${task.id}`} pages={pages} />;
}

// ============================================================================
// Kanban tab
// ============================================================================
function ProjectsKanban({ tree, people, scope, reload }) {
  const [groupBy, setGroupBy] = useState("status");
  const [q, setQ] = useState("");

  const scopedProjectIds = useMemo(() => {
    if (scope === "all") return new Set(tree.projects.map((p) => p.id));
    if (scope.startsWith("vertical:")) {
      const fid = scope.split(":")[1];
      return new Set(tree.projects.filter((p) => p.folder_id === fid).map((p) => p.id));
    }
    if (scope.startsWith("category:")) {
      const gid = scope.split(":")[1];
      return new Set(tree.projects.filter((p) => p.group_id === gid).map((p) => p.id));
    }
    if (scope.startsWith("project:")) {
      const pid = scope.split(":")[1];
      return new Set([pid]);
    }
    return new Set(tree.projects.map((p) => p.id));
  }, [tree.projects, scope]);

  const filteredTasks = useMemo(() => {
    let tasks = tree.tasks.filter((t) => !t.parent_task_id && t.project_id && scopedProjectIds.has(t.project_id));
    if (q.trim()) tasks = tasks.filter((t) => t.title.toLowerCase().includes(q.toLowerCase()));
    return tasks;
  }, [tree.tasks, scopedProjectIds, q]);

  const columns = useMemo(() => {
    if (groupBy === "status") return [
      { key: "todo", title: "To do", color: "#A3A3A3" },
      { key: "in_progress", title: "In progress", color: "#0A0A0A" },
      { key: "blocked", title: "Blocked", color: "#FF453A" },
      { key: "done", title: "Done", color: "#10B981" },
    ];
    if (groupBy === "priority") return [
      { key: "critical", title: "Critical", color: "#FF453A" },
      { key: "high", title: "High", color: "#FF9F0A" },
      { key: "medium", title: "Medium", color: "#0A0A0A" },
      { key: "low", title: "Low", color: "#A3A3A3" },
    ];
    const owners = Array.from(new Set(filteredTasks.map((t) => t.owner || "Unassigned"))).sort();
    return owners.map((o) => ({ key: o, title: o, color: o === "Unassigned" ? "#A3A3A3" : "#0A0A0A" }));
  }, [groupBy, filteredTasks]);

  const cardsByColumn = useMemo(() => {
    const m = {};
    columns.forEach((c) => (m[c.key] = []));
    filteredTasks.forEach((t) => {
      const k = groupBy === "status" ? t.status : groupBy === "priority" ? t.priority : (t.owner || "Unassigned");
      if (!m[k]) m[k] = [];
      m[k].push(t);
    });
    return m;
  }, [columns, filteredTasks, groupBy]);

  const onMove = async (taskId, fromKey, toKey) => {
    if (fromKey === toKey) return;
    const patch = groupBy === "status"
      ? { status: toKey, completed_at: toKey === "done" ? new Date().toISOString() : null }
      : groupBy === "priority" ? { priority: toKey }
      : { owner: toKey === "Unassigned" ? null : toKey };
    await endpoints.updateTask(taskId, patch);
    toast.success(groupBy === "status" ? `Moved to ${toKey.replace("_", " ")}` : groupBy === "priority" ? `Priority: ${toKey}` : `Owner: ${toKey}`);
    reload();
  };

  const renderKebab = (card) => {
    const STATUS_LABEL = { todo: "To do", in_progress: "In progress", blocked: "Blocked", done: "Done" };
    const ownerLabel = card.owner || "Unassigned";
    const priorityLabel = card.priority ? card.priority[0].toUpperCase() + card.priority.slice(1) : "Medium";
    const statusLabel = STATUS_LABEL[card.status] || card.status;
    const pages = {
      root: {
        items: [
          { label: "Status", subtitle: statusLabel, toPage: "status" },
          { label: "Priority", subtitle: priorityLabel, toPage: "priority" },
          { label: "Assign to", subtitle: ownerLabel, toPage: "owner" },
          { divider: true },
          { label: "Delete task", icon: <X size={13} />, danger: true,
            onClick: async () => { await endpoints.deleteTask(card.id); toast.success("Task deleted"); reload(); } },
        ],
      },
      status: {
        title: "Status",
        items: STATUS_OPTS.map((s) => ({
          label: STATUS_LABEL[s], active: card.status === s,
          onClick: async () => { await endpoints.updateTask(card.id, { status: s, completed_at: s === "done" ? new Date().toISOString() : null }); reload(); },
        })),
      },
      priority: {
        title: "Priority",
        items: PRIORITY_OPTS.map((p) => ({
          label: p[0].toUpperCase() + p.slice(1), active: card.priority === p,
          onClick: async () => { await endpoints.updateTask(card.id, { priority: p }); reload(); },
        })),
      },
      owner: {
        title: "Assign to",
        searchable: true,
        searchPlaceholder: "Search people…",
        items: [
          { label: "Unassigned", active: !card.owner, onClick: async () => { await endpoints.updateTask(card.id, { owner: null }); reload(); } },
          ...(people || []).map((p) => ({
            label: p.name, active: card.owner === p.name,
            onClick: async () => { await endpoints.updateTask(card.id, { owner: p.name }); reload(); },
          })),
        ],
      },
    };
    return <PagedKebabMenu testid={`tasks-kanban-kebab-${card.id}`} pages={pages} />;
  };

  const renderCard = (card) => {
    const proj = tree.projects.find((p) => p.id === card.project_id);
    const folder = tree.folders.find((f) => f.id === proj?.folder_id);
    return (
      <div>
        <p className="text-sm text-neutral-900 leading-snug">{card.title}</p>
        <div className="flex items-center flex-wrap gap-2 mt-1.5 text-[10px]">
          {proj && (
            <span
              className="inline-flex items-center gap-1 mono px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-700"
              data-testid={`kanban-card-project-tag-${card.id}`}
            >
              <span className="h-1.5 w-1.5 rounded-sm" style={{ background: folder?.color || "#A3A3A3" }} />
              {proj.name}
            </span>
          )}
          {card.owner && <PersonLink name={card.owner} className="text-[10px]" />}
          {card.priority && card.priority !== "medium" && (
            <span className={`mono ${card.priority === "critical" ? "text-[#FF453A]" : card.priority === "high" ? "text-[#FF9F0A]" : "text-neutral-500"}`}>
              {card.priority}
            </span>
          )}
          {card.due_date && <span className="text-neutral-500">Due {formatDate(card.due_date)}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <ScopeChip tree={tree} scope={scope} />
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          className="text-sm px-2 py-1.5 rounded-sm border border-neutral-200 bg-white"
          data-testid="kanban-groupby"
        >
          <option value="status">Group by: Status</option>
          <option value="priority">Group by: Priority</option>
          <option value="assignee">Group by: Assignee</option>
        </select>
        <div className="flex-1 min-w-[160px] flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 bg-white">
          <Search size={12} className="text-neutral-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tasks…" className="flex-1 bg-transparent outline-none text-sm" data-testid="kanban-search" />
        </div>
        <span className="text-xs text-neutral-500">{filteredTasks.length} tasks</span>
      </div>
      <div className="flex-1 overflow-auto">
        <KanbanBoard
          testid="tasks-kanban"
          columns={columns}
          cardsByColumn={cardsByColumn}
          onMove={onMove}
          renderCard={renderCard}
          renderKebab={renderKebab}
        />
      </div>
    </div>
  );
}

function ScopeChip({ tree, scope }) {
  let label = "All Projects";
  if (scope.startsWith("vertical:")) {
    const f = tree.folders.find((x) => x.id === scope.split(":")[1]);
    label = f ? `Vertical · ${f.name}` : "Vertical";
  } else if (scope.startsWith("category:")) {
    const g = tree.groups.find((x) => x.id === scope.split(":")[1]);
    label = g ? `Category · ${g.name}` : "Category";
  } else if (scope.startsWith("project:")) {
    const p = tree.projects.find((x) => x.id === scope.split(":")[1]);
    label = p ? p.name : "Project";
  }
  return (
    <span className="text-xs font-medium px-3 py-1.5 rounded-sm bg-neutral-100 text-neutral-800" data-testid="kanban-scope-chip">
      {label}
    </span>
  );
}

// ============================================================================
// SOP-as-task modal
// ============================================================================
function SopTaskModal({ sops, people, onClose, onSubmit }) {
  const [sopId, setSopId] = useState(sops[0]?.id || "");
  const [priority, setPriority] = useState("medium");
  const [owner, setOwner] = useState("");
  const [parentTitle, setParentTitle] = useState("");
  const [steps, setSteps] = useState([]);
  useEffect(() => {
    if (!sopId) { setSteps([]); return; }
    endpoints.sopSteps(sopId).then(setSteps).catch(() => setSteps([]));
  }, [sopId]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="fos-card p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="modal-sop-task">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Add task from SOP</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-neutral-100"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-1">SOP template</p>
        <select value={sopId} onChange={(e) => setSopId(e.target.value)}
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white mb-3" data-testid="sop-task-sop">
          {sops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <p className="text-xs text-neutral-500 mb-1">Task title <span className="text-neutral-400">(defaults to SOP name)</span></p>
        <input value={parentTitle} onChange={(e) => setParentTitle(e.target.value)} placeholder={sops.find((s) => s.id === sopId)?.name || ""}
          className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white mb-3" data-testid="sop-task-title" />
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <p className="text-xs text-neutral-500 mb-1">Priority</p>
            <select value={priority} onChange={(e) => setPriority(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white">
              {PRIORITY_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs text-neutral-500 mb-1">Owner</p>
            <select value={owner} onChange={(e) => setOwner(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-sm border border-neutral-200 bg-white">
              <option value="">Unassigned</option>
              {people.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="rounded-sm border border-neutral-200 bg-neutral-50 p-3 mb-3">
          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-2">
            Will create {steps.length} subtask{steps.length === 1 ? "" : "s"}:
          </p>
          <ul className="space-y-1 max-h-40 overflow-y-auto">
            {steps.map((s) => (
              <li key={s.id} className="text-xs text-neutral-700 flex items-center gap-2">
                <span className="text-neutral-400">{String(s.step_number).padStart(2, "0")}</span>
                <span className="truncate">{s.title}</span>
              </li>
            ))}
            {steps.length === 0 && <li className="text-[11px] italic text-neutral-400">No steps in this SOP.</li>}
          </ul>
        </div>
        <button onClick={() => onSubmit({ sop_id: sopId, priority, owner, parent_title: parentTitle })}
          disabled={!sopId}
          className="w-full text-sm px-3 py-2 rounded-sm bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-40"
          data-testid="sop-task-submit">
          Add task + {steps.length} subtask{steps.length === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}
